import {
  updateParcel,
  dimensionsSelector,
  isMultiboxSelector,
  dimensionsErrorMessageSelector,
  dimensionHasError,
  dimensionsWarningMessageSelector,
  showDimensionsSelector,
  labelPropertiesSelector,
  multiboxShipperTypesSelector,
  formattedDimensionsSelector,
  isLabelPropertyEnabledSelector,
} from '../../../../data/labelInfos/index.js'
import {showMultiboxModal} from '../../../LabelConfig/Modals/MultiboxModal/index.js'

import DimensionsInput from '../../../../common/components/Form/DimensionsInput.js'
import EnableLabelProperty, {PickLabelProperty} from '../EnableLabelProperty.js'
import {getState, useSelector} from '../../../../store.js'
import useLabelProperty, {
  EMPTY_DEPENDENT_PROPERTIES_FUNC,
  formatMetaClassName,
} from '../useLabelProperty.js'
import className from '../../../../common/className.js'
import {useLabelConfigContext} from '../../LabelConfigContext.js'
import ButtonLink from '../../../../common/components/Button/ButtonLink.js'
import {showEditWeightDimensionsModal} from '../../Modals/EditWeightDimensionsModal.js'
import {SHIPPER_VENDORS} from '../../../../common/constants/ShipperNames.js'

const LENGTH_LABEL_PROPERTY = 'packages[0].length'
const WIDTH_LABEL_PROPERTY = 'packages[0].width'
const HEIGHT_LABEL_PROPERTY = 'packages[0].height'
const VALID_LABEL_PROPERTIES = [
  LENGTH_LABEL_PROPERTY,
  WIDTH_LABEL_PROPERTY,
  HEIGHT_LABEL_PROPERTY,
]

function labelPropertiesFunc() {
  return VALID_LABEL_PROPERTIES
}

export function isLabelIncludesDimensionsSelector(state, {labelInfoID}) {
  const properties = labelPropertiesSelector(state, {labelInfoID})

  return (
    properties.includes(LENGTH_LABEL_PROPERTY) &&
    properties.includes(WIDTH_LABEL_PROPERTY) &&
    properties.includes(HEIGHT_LABEL_PROPERTY)
  )
}

export default function SingleDimensions({packagesIndex = 0}) {
  const {labelInfoID, onChange, isLabelEnableMode, isSummary, isPickMode} =
    useLabelConfigContext()
  const propertySpecs = SingleDimensions.propertySpecList[0]
  const [labelProperty] = propertySpecs.labelPropertiesFunc(null, labelInfoID)

  const {length, width, height} = useSelector((state) =>
    dimensionsSelector(state, {
      labelInfoID,
      packagesIndex,
    }),
  )
  const formattedDimensions = useSelector((state) =>
    formattedDimensionsSelector(state, {labelInfoID}),
  )
  const showDimensions = useSelector((state) =>
    showDimensionsSelector(state, {labelInfoID}),
  )
  const warningMessage = useSelector((state) =>
    dimensionsWarningMessageSelector(state, {labelInfoID}),
  )
  const errorMessage = useSelector((state) =>
    dimensionsErrorMessageSelector(state, {labelInfoID}),
  )
  const isMultibox = useSelector((state) =>
    isMultiboxSelector(state, {labelInfoID}),
  )
  const showSummary = !isLabelEnableMode

  useLabelProperty({
    labelProperty,
    propertySpecs,
  })

  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty,
    }),
  )

  if (!showDimensions && !isMultibox && !isLabelEnableMode) {
    return null
  }

  const label = 'Dimensions'

  if (isSummary) {
    return label
  }

  if (isPickMode && !isLabelPropertyEnabled) {
    return <PickLabelProperty label={label} labelProperty={labelProperty} />
  }

  const disabled = isMultibox || (isLabelEnableMode && !isLabelPropertyEnabled)
  const hasLengthError = dimensionHasError(length)
  const hasWidthError = dimensionHasError(width)
  const hasHeightError = dimensionHasError(height)

  return (
    <li
      className={className`list__item--shipping-options btm-offset-7 ${{
        error: !!errorMessage && !disabled,
      }} ${formatMetaClassName(labelProperty)}`}
    >
      <div className="wrap--edit-preset-form-input wrap--md-number-input">
        <div className="flex">
          <label className="label--bold" htmlFor="dimensions_length">
            {label}
          </label>
          {isMultibox && (
            <button
              className="btn btn--link margin-bottom-3 margin-left-3 fs-n0"
              type="button"
              onClick={() => {
                const [shipperType] = multiboxShipperTypesSelector(getState(), {
                  labelInfoID,
                })
                showMultiboxModal(labelInfoID, shipperType)
              }}
            >
              <em>(Multiple)</em>
            </button>
          )}
        </div>
        {showSummary ? (
          <div className="wrap--single-dim wrap--dim-summary margin-bottom-15">
            <ButtonLink
              className="darker v-align-base border-underline"
              onClick={() =>
                showEditWeightDimensionsModal({
                  labelInfoIDs: [labelInfoID],
                  packagesIndex,
                  focusOn: 'dimensions',
                })
              }
              disabled={disabled}
            >
              <strong className="fs-02 lh-sm-md">
                {formattedDimensions || <em>Not set</em>}
              </strong>
            </ButtonLink>
            {errorMessage && (
              <pre className="wrap--error-wb lh-sm">
                <small className="error margin-top-0">{errorMessage}</small>
              </pre>
            )}
            {!errorMessage && warningMessage && (
              <pre className="wrap--error-wb lh-sm">
                <small className="warning margin-top-0">{warningMessage}</small>
              </pre>
            )}
          </div>
        ) : (
          <DimensionsInput
            length={isMultibox ? '' : length}
            width={isMultibox ? '' : width}
            height={isMultibox ? '' : height}
            setLength={(length) => {
              updateParcel(labelInfoID, packagesIndex, {length})
              onChange()
            }}
            setWidth={(width) => {
              updateParcel(labelInfoID, packagesIndex, {width})
              onChange()
            }}
            setHeight={(height) => {
              updateParcel(labelInfoID, packagesIndex, {height})
              onChange()
            }}
            errorMessage={errorMessage}
            hasLengthError={hasLengthError}
            hasWidthError={hasWidthError}
            hasHeightError={hasHeightError}
            warningMessage={warningMessage}
            disabled={disabled}
          />
        )}
      </div>
      <EnableLabelProperty
        checkedSelector={(state) =>
          isLabelIncludesDimensionsSelector(state, {labelInfoID})
        }
        labelProperty={labelProperty}
      />
    </li>
  )
}

SingleDimensions.propertySpecList = [
  {
    validShipperTypes: SHIPPER_VENDORS,
    labelPropertiesFunc,
    dependentPropertiesFunc: EMPTY_DEPENDENT_PROPERTIES_FUNC,
    validLabelPropertiesFunc() {
      return VALID_LABEL_PROPERTIES
    },
  },
]
